/**
 * GoLive Source Configuration Utility
 *
 * This module provides utilities for resolving which filter level (product, model, sector, market, plant)
 * should be used to determine the goLiveDate for date picker restrictions.
 *
 * The routing rules are configured in DatoCMS on the Product model via:
 * - goLiveSource: Single-line string enum (product | model | sector | market | plant)
 * - goLiveSourceRules: JSON field for conditional routing (e.g., Guided Selling per-model rules)
 */

/**
 * Valid goLiveDate source levels
 */
export type GoLiveSourceLevel =
  | 'product'
  | 'model'
  | 'sector'
  | 'market'
  | 'plant';

/**
 * Conditional rules for products that route differently based on modelId
 * Example: Guided Selling routes to 'market' for some models, 'model' for others
 */
export interface GoLiveSourceRules {
  default?: GoLiveSourceLevel;
  byModelId?: Record<string | number, GoLiveSourceLevel>;
}

/**
 * CMS configuration for goLiveSource on a product
 */
export interface GoLiveSourceConfig {
  goLiveSource?: GoLiveSourceLevel;
  goLiveSourceRules?: GoLiveSourceRules;
}

/**
 * Filter context values needed for goLiveDate resolution
 */
export interface FilterSelections {
  selectedProduct?: {
    productId?: number;
    goLiveDate?: string;
  };
  selectedModel?: {
    id?: number;
    modelId?: number;
    goLiveDate?: string;
  };
  selectedSector?: {
    sectorId?: number;
    goLiveDate?: string;
  };
  selectedMarket?: {
    marketId?: number;
    goLiveDate?: string;
  };
  selectedPlant?: {
    plantId?: number;
    goLiveDate?: string;
  };
}

/**
 * Default source level when no CMS config is available
 */
export const DEFAULT_GO_LIVE_SOURCE: GoLiveSourceLevel = 'product';

/**
 * Resolves which goLiveSource level to use based on CMS config and current model selection
 *
 * @param config - CMS configuration from DatoCMS (goLiveSource and goLiveSourceRules)
 * @param modelId - Currently selected model ID (for conditional routing)
 * @returns The resolved goLiveSource level
 *
 * @example
 * // Simple product-level config
 * resolveGoLiveSource({ goLiveSource: 'market' }, null) // => 'market'
 *
 * @example
 * // Conditional routing for Guided Selling
 * const config = {
 *   goLiveSource: 'market',
 *   goLiveSourceRules: {
 *     default: 'market',
 *     byModelId: { 2: 'model', 3: 'model' } // shareWise, productPairing
 *   }
 * };
 * resolveGoLiveSource(config, 2) // => 'model' (shareWise)
 * resolveGoLiveSource(config, 1) // => 'market' (suggestedOrder)
 */
export const resolveGoLiveSource = (
  config: GoLiveSourceConfig | null | undefined,
  modelId: number | string | null | undefined
): GoLiveSourceLevel => {
  if (!config) {
    return DEFAULT_GO_LIVE_SOURCE;
  }

  const { goLiveSource, goLiveSourceRules } = config;

  // Check for conditional rules based on modelId
  if (goLiveSourceRules?.byModelId && modelId != null) {
    const modelSpecificSource = goLiveSourceRules.byModelId[modelId];
    if (modelSpecificSource) {
      return modelSpecificSource;
    }
    // Fall back to rules.default if modelId not in byModelId map
    if (goLiveSourceRules.default) {
      return goLiveSourceRules.default;
    }
  }

  // Use the simple goLiveSource if no conditional rules apply
  if (goLiveSource) {
    return goLiveSource;
  }

  return DEFAULT_GO_LIVE_SOURCE;
};

/**
 * Extracts the goLiveDate value from filter selections based on the resolved source level
 *
 * @param source - The resolved goLiveSource level
 * @param selections - Current filter selections from FilterContext
 * @returns The goLiveDate string from the appropriate filter level, or null if not found
 */
export const getGoLiveDateFromSource = (
  source: GoLiveSourceLevel,
  selections: FilterSelections
): string | null | undefined => {
  switch (source) {
    case 'product':
      return selections.selectedProduct?.goLiveDate;
    case 'model':
      return selections.selectedModel?.goLiveDate;
    case 'sector':
      return selections.selectedSector?.goLiveDate;
    case 'market':
      return selections.selectedMarket?.goLiveDate;
    case 'plant':
      return selections.selectedPlant?.goLiveDate;
    default:
      return null;
  }
};

/**
 * Parses a goLiveDate string into a valid Date object
 * Falls back to current date if parsing fails
 *
 * @param dateString - ISO date string or any parseable date format
 * @returns Valid Date object
 */
export const parseGoLiveDate = (
  dateString: string | null | undefined
): Date => {
  if (!dateString) {
    return new Date();
  }

  const parsed = new Date(dateString);
  if (Number.isNaN(parsed.getTime()) || parsed.toString() === 'Invalid Date') {
    return new Date();
  }

  return parsed;
};

/**
 * Complete resolution: determines source level, extracts date, and parses it
 *
 * @param config - CMS configuration from DatoCMS
 * @param modelId - Currently selected model ID
 * @param selections - Current filter selections
 * @returns Object with resolved source level and parsed Date
 */
export const resolveGoLiveDate = (
  config: GoLiveSourceConfig | null | undefined,
  modelId: number | string | null | undefined,
  selections: FilterSelections
): { source: GoLiveSourceLevel; date: Date } => {
  const source = resolveGoLiveSource(config, modelId);
  const dateString = getGoLiveDateFromSource(source, selections);
  const date = parseGoLiveDate(dateString);

  return { source, date };
};

/**
 * Validates if a goLiveSource value is valid
 */
export const isValidGoLiveSource = (
  value: unknown
): value is GoLiveSourceLevel => {
  return (
    typeof value === 'string' &&
    ['product', 'model', 'sector', 'market', 'plant'].includes(value)
  );
};
